/**
 * STK-352 — recap detail action bar.
 *
 * Thin renderer for the focus detail page. It classifies the pre-order
 * (`classifyRecapState`), looks up the visible buttons (`resolveButtons`), and
 * wires each one to a transition command (`useRecapTransitions`). No lifecycle
 * logic lives here — only rendering + the disabled/busy gating that the legacy
 * components already applied.
 *
 * `useRecapActionBar` returns the destructive `left` cluster, the confirmation
 * `main` cluster, and the `dialogs` separately, so the focus hero bar can place
 * the destructive actions far-left and the confirmation actions far-right
 * (Constitution-friendly separation) while calling the transition hook ONCE.
 * `<RecapActionBar>` is the one-flex convenience wrapper for simpler surfaces.
 * `<RecapModifyCancelSlot>` is the table-header "Cancel modifications" chip.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@stockline/ui/components/alert-dialog";
import { XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@stockline/ui/components/button";
import { cn } from "@/lib/utils";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import type { PreOrder } from "@/app/api/types/preOrder";
import { useAiPreOrderActions } from "@/app/ai-pre-orders/hooks/useAiPreOrderActions";
import type { ModifyBufferApi } from "../hooks/useModifyBuffer";
import { usePreOrderLock } from "../hooks/usePreOrderLock";
import { useIsPreOrderConfirming } from "../stores/confirmInFlightStore";
import { ExportErrorPopover } from "../components/ui/ExportErrorPopover";
import { ModifyErrorPopover } from "../components/ui/ModifyErrorPopover";
import { IllOrdersPopover } from "../components/ui/IllOrdersPopover";
import { useAiInFlightSlice } from "../stores/aiInFlightStore";
import { classifyRecapState } from "./state";
import { resolveButtons, type ButtonSpec } from "./buttonTable";
import { runAcceptAiPending, useRecapTransitions } from "./transitions";
import { ActionButton, type ActionBarVariant } from "./ActionButton";

export interface RecapActionBarProps {
  preOrder: PreOrder;
  /** Published up from `<PreOrderSection>`; null for the first frame. */
  buffer: ModifyBufferApi | null;
  onDelete?: () => void;
  onRestore?: () => void;
  variant?: ActionBarVariant;
}

export interface RecapActionBarRender {
  /** Destructive / reject cluster (focus far-left slot). */
  left: React.ReactNode;
  /** Confirmation cluster (focus far-right slot). */
  main: React.ReactNode;
  /** Portalled dialogs — render once anywhere. */
  dialogs: React.ReactNode;
  /** True when any button (left or main) is present. */
  hasActions: boolean;
}

export function useRecapActionBar({
  preOrder,
  buffer,
  onDelete,
  onRestore,
  variant = "hero",
}: RecapActionBarProps): RecapActionBarRender {
  const { t } = useTranslation();
  const modifyModeFlag = useFeatureFlag("recap_modify_mode") === true;
  const exportMode = useFeatureFlag("export_mode");
  // No ERP export → the confirm button says "Confirmer", not "Exporter vers
  // l'ERP" (STK-1761).
  const isNoExport = !exportMode || exportMode === "none";
  const storeConfirming = useIsPreOrderConfirming(preOrder);
  const lock = usePreOrderLock({
    preOrder,
    isFlushing: buffer?.isFlushing ?? false,
  });
  const transitions = useRecapTransitions({
    preOrder,
    buffer,
    onDelete,
    onRestore,
  });
  const inModifyMode = buffer?.isModifying ?? false;
  const hasUserMods = (buffer?.pendingInfo.total ?? 0) > 0;
  const base = classifyRecapState({ preOrder, inModifyMode, hasUserMods });
  // The local optimistic-confirm store covers the click → SSE `in_progress`
  // gap that the pure classifier can't see.
  const state =
    storeConfirming && base.exportPhase === "idle"
      ? { ...base, exportPhase: "confirming" as const }
      : base;

  const { left, main } = resolveButtons(state, modifyModeFlag, isNoExport);
  const locked = lock.isLocked;

  const mainDisabled = (spec: ButtonSpec): boolean => {
    if (locked || transitions.busy) return true;
    if (spec.requiresUserMods && !hasUserMods) return true;
    return false;
  };

  // Deletion is confirmed by the parent (`PreOrderFocusView`'s delete dialog),
  // so the "warn before losing unsaved mods" gate (spec case 4) is satisfied
  // there — no second dialog here.
  const handleLeftClick = (spec: ButtonSpec) => {
    if (spec.transition) transitions.commands[spec.transition]();
  };

  const renderMain = (spec: ButtonSpec) => {
    // Failed / ill pills are popover triggers: the button opens the popover,
    // whose Retry / Heal CTA runs the transition (not the click itself).
    if (spec.popover === "failed") {
      return (
        <ExportErrorPopover
          key={spec.id}
          failedOrders={transitions.failed.orders}
          onRetry={transitions.failed.onRetry}
          isRetrying={transitions.failed.isRetrying}
        >
          <span>
            <ActionButton
              spec={spec}
              variant={variant}
              onClick={() => {}}
              stopPropagation={false}
            />
          </span>
        </ExportErrorPopover>
      );
    }
    if (spec.popover === "ill") {
      return (
        <IllOrdersPopover
          key={spec.id}
          illOrders={transitions.ill.orders}
          onHeal={transitions.ill.onHeal}
          isHealing={transitions.ill.isHealing}
          canHeal={transitions.ill.canHeal}
          onRetryExport={() =>
            transitions.failed.onRetry(transitions.ill.orders)
          }
          isRetryingExport={transitions.ill.isRetrying}
        >
          <span>
            <ActionButton
              spec={spec}
              variant={variant}
              onClick={() => {}}
              stopPropagation={false}
            />
          </span>
        </IllOrdersPopover>
      );
    }
    // Static pills (confirmed / exported / confirming) have no transition.
    const onClick = spec.transition
      ? () => transitions.commands[spec.transition!]()
      : undefined;
    return (
      <ActionButton
        key={spec.id}
        spec={spec}
        variant={variant}
        onClick={onClick}
        disabled={onClick ? mainDisabled(spec) : false}
        busy={transitions.busy && spec.tone !== "confirming"}
      />
    );
  };

  const leftNode = left.map((spec) => (
    <ActionButton
      key={spec.id}
      spec={spec}
      variant={variant}
      onClick={() => handleLeftClick(spec)}
      disabled={locked || transitions.busy}
      busy={transitions.busy}
    />
  ));

  const dialogs = (
    <>
      {/* Blocked-partner warning (M3 stop-client / credit limit). */}
      <AlertDialog
        open={transitions.blocked.open}
        onOpenChange={transitions.blocked.setOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ordersRecap.partnerAccount.blockedExportTitle", {
                defaultValue: "Partenaire bloqué dans M3",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ordersRecap.partnerAccount.blockedExportDescription", {
                defaultValue:
                  "Le compte de ce client est actuellement bloqué. L'export va probablement échouer côté M3.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel", { defaultValue: "Annuler" })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={transitions.blocked.onContinue}>
              {t("ordersRecap.partnerAccount.blockedExportContinue", {
                defaultValue: "Continuer quand même",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return {
    left: leftNode,
    main: main.map(renderMain),
    dialogs,
    hasActions: left.length > 0 || main.length > 0,
  };
}

/** One-flex convenience wrapper (left + main + dialogs together). */
export function RecapActionBar(props: RecapActionBarProps) {
  const { left, main, dialogs } = useRecapActionBar(props);
  return (
    <div className="flex items-center gap-2" data-testid="recap-action-bar">
      {left}
      {main}
      {dialogs}
    </div>
  );
}

interface RecapModifyCancelSlotProps {
  preOrder: PreOrder;
  buffer: ModifyBufferApi | null;
}

/**
 * Local chips for the flows-table header — **Cancel modifications** plus, for
 * non-confirmed rows, EITHER **Save** or **Accept AI** in the same slot. None
 * of them hits ERP: Cancel discards the buffer, Save persists edits via
 * `buffer.flushSave` (which stays local for PENDING / AI_PROPOSAL), Accept AI
 * resolves the pending AI signals and keeps the order PENDING (promoting an
 * AI_PROPOSAL parent). Standalone — it needs the buffer and the AI actions
 * hook, never the transition hook, so the local actions stay trivially
 * independent of the status-change actions in the hero header.
 */
export function RecapModifyCancelSlot({
  preOrder,
  buffer,
}: RecapModifyCancelSlotProps) {
  const { t } = useTranslation();
  const modifyModeFlag = useFeatureFlag("recap_modify_mode") === true;
  const exportMode = useFeatureFlag("export_mode");
  // No ERP export → the confirm button says "Confirmer", not "Exporter vers
  // l'ERP" (STK-1761).
  const isNoExport = !exportMode || exportMode === "none";
  const aiActions = useAiPreOrderActions(preOrder.id);
  // Spinner state from the SHARED per-PO store (same source the hero bar and
  // usePreOrderLock read), not this instance's aiActions flags.
  const { isApprovingAi } = useAiInFlightSlice(preOrder.id);
  const lock = usePreOrderLock({
    preOrder,
    isFlushing: buffer?.isFlushing ?? false,
  });
  const inModifyMode = buffer?.isModifying ?? false;
  const hasUserMods = (buffer?.pendingInfo.total ?? 0) > 0;
  const state = classifyRecapState({ preOrder, inModifyMode, hasUserMods });
  const { headerSlot } = resolveButtons(state, modifyModeFlag, isNoExport);
  if (!buffer || headerSlot.length === 0) return null;

  const busy = buffer.isFlushing || lock.isLocked;
  const lastError = buffer.lastError;
  const retrySave = (): void => {
    // Commit a typed-but-unfocused cell into the buffer first.
    (document.activeElement as HTMLElement | null)?.blur?.();
    void buffer.flushSave();
  };
  const onClickFor = (spec: ButtonSpec): (() => void) => {
    if (spec.transition === "saveModifications") return retrySave;
    // Same implementation the hero's transition command delegates to — the
    // slot only supplies its own aiActions instance.
    if (spec.transition === "acceptAiPending")
      return () => runAcceptAiPending(aiActions, preOrder);
    return () => buffer.cancelModify();
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="recap-modify-bar">
      {headerSlot.map((spec) => {
        // A failed flushSave swaps the Save chip for a retry-able failed
        // badge — restores the modify-save failure surface the legacy
        // list-view `ModifyControls` had but the #1505 focus-view migration
        // dropped, so a focus-view save error is visible + retryable rather
        // than silent. Cancel stays so the buffer can still be discarded.
        if (spec.transition === "saveModifications" && lastError) {
          return (
            <ModifyErrorPopover
              key={spec.id}
              message={lastError.message}
              onRetry={retrySave}
              isRetrying={busy}
            >
              <Button
                size="sm"
                data-testid="recap-modify-failed"
                data-attr="recap-modify-failed"
                className={cn(
                  "h-7 gap-1.5 text-xs",
                  "bg-red-50 hover:bg-red-100 text-red-700 border border-red-300",
                )}
              >
                <XCircle className="h-3.5 w-3.5" />
                {t("ordersRecap.modifyFailed")}
                {hasUserMods && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[10px] font-semibold text-red-700">
                    {buffer.pendingInfo.total}
                  </span>
                )}
              </Button>
            </ModifyErrorPopover>
          );
        }
        return (
          <ActionButton
            key={spec.id}
            spec={spec}
            variant="default"
            onClick={onClickFor(spec)}
            disabled={busy || (spec.requiresUserMods && !hasUserMods)}
            busy={spec.transition === "acceptAiPending" && isApprovingAi}
          />
        );
      })}
    </div>
  );
}
